import { Wallet, Camera, RefreshCcw, Sparkles, Check } from "lucide-react";
import { Seo } from "@/components/common/Seo";
import { PageHero } from "@/components/common/PageHero";
import { Reveal, RevealGroup, RevealItem } from "@/components/common/Reveal";
import { SectionLabel } from "@/components/common/SectionLabel";
import { CTAButton } from "@/components/common/CTAButton";
import { LeadForm } from "@/components/common/LeadForm";
import { EditorialMarquee } from "@/components/common/EditorialMarquee";
import {
  SEO, WHY_DOOH, CAMPAIGN_LIFECYCLE, ROADMAP, LENGTH_MULTIPLIERS, FREQ_MULTIPLIERS, IMAGES, ADVERTISER_FAQ, REGISTER_URL,
} from "@/data/site";

const WhyDooh = () => (
  <section className="bg-paper py-24 md:py-32">
    <div className="container-px">
      <Reveal><SectionLabel>Why Residential DOOH</SectionLabel></Reveal>
      <Reveal delay={0.05}>
        <h2 className="mt-6 font-display font-800 text-4xl md:text-6xl tracking-tight max-w-3xl text-balance">
          Attention where it actually lives.
        </h2>
      </Reveal>
      <RevealGroup className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-px bg-ink/10 border border-ink/10 rounded-xl overflow-hidden">
        {WHY_DOOH.map((s) => (
          <RevealItem key={s.label} className="bg-white p-7">
            <p className="font-display font-800 text-4xl md:text-5xl text-teal tracking-tight">{s.stat}</p>
            <p className="mt-4 font-display font-600">{s.label}</p>
            <p className="mt-2 text-sm text-ink/55 leading-relaxed">{s.note}</p>
          </RevealItem>
        ))}
      </RevealGroup>
    </div>
  </section>
);

const PricingHow = () => (
  <section className="bg-white py-24 md:py-32 border-y border-ink/10">
    <div className="container-px">
      <Reveal><SectionLabel>How Pricing Works</SectionLabel></Reveal>
      <Reveal delay={0.05}>
        <h2 className="mt-6 font-display font-800 text-4xl md:text-6xl tracking-tight max-w-3xl text-balance">
          You pay per impression. Nothing hidden.
        </h2>
      </Reveal>

      <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <RevealGroup className="space-y-6">
            {[
              ["Choose your ad length", "5 to 30 seconds, in 5-second increments."],
              ["Choose your frequency", "How often your ad plays - every 1, 2, 3 or 5 minutes."],
              ["See your transparent rate", "The calculator prices your campaign from length, frequency and communities - no maths required."],
              ["Pay per screen impression", "An impression is counted each time your ad plays on an operational screen."],
            ].map((s, i) => (
              <RevealItem key={i} className="flex gap-5">
                <span className="font-display font-800 text-2xl text-ink/20 tabular-nums">0{i + 1}</span>
                <div>
                  <h4 className="font-display font-700 text-lg">{s[0]}</h4>
                  <p className="mt-2 text-ink/60">{s[1]}</p>
                </div>
              </RevealItem>
            ))}
          </RevealGroup>
          <Reveal delay={0.1}>
            <div className="mt-10 rounded-lg bg-ink text-white p-6">
              <p className="font-mono text-xs uppercase tracking-[0.18em] text-teal">Starts at</p>
              <p className="mt-2 font-display font-800 text-3xl">1p <span className="text-white/50 text-lg font-500">/ second / screen</span></p>
              <p className="mt-2 text-sm text-white/50">Pricing starts at just 1 paisa per second.</p>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div className="rounded-xl border border-ink/10 overflow-hidden">
            <div className="bg-paper px-5 py-3 font-mono text-xs uppercase tracking-[0.18em] text-ink/50">Ad Lengths</div>
            <table className="w-full text-sm">
              <tbody>
                {LENGTH_MULTIPLIERS.map((r) => (
                  <tr key={r.len} className="border-t border-ink/10">
                    <td className="px-5 py-3 font-display font-600">{r.len}</td>
                    <td className="px-5 py-3 text-right text-ink/50">{r.slots}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="rounded-xl border border-ink/10 overflow-hidden">
            <div className="bg-paper px-5 py-3 font-mono text-xs uppercase tracking-[0.18em] text-ink/50">Frequency Options</div>
            <table className="w-full text-sm">
              <tbody>
                {FREQ_MULTIPLIERS.map((r) => (
                  <tr key={r.freq} className="border-t border-ink/10">
                    <td className="px-5 py-3 font-display font-600">{r.freq}</td>
                    <td className="px-5 py-3 text-right text-ink/50">{r.desc}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <Reveal delay={0.1}>
        <div className="mt-12">
          <CTAButton to="/pricing" variant="dark" data-testid="advertiser-calc-cta">Try the pricing calculator</CTAButton>
        </div>
      </Reveal>
    </div>
  </section>
);

const featureCards = [
  { icon: Wallet, title: "The Prepaid Wallet", body: "No surprises, no invoices, no negotiations. Load your wallet, book a campaign, and watch impressions deduct in real time. Auto-pauses on a low balance; resume manually after a recharge." },
  { icon: Camera, title: "Playback Evidence", body: "Detailed campaign and playback records support transparent reporting, giving you visibility into where your advertising ran." },
  { icon: RefreshCcw, title: "Delivery Compensation", body: "Where campaign delivery is affected by screen availability, AdX applies its defined compensation policy - so you pay for the advertising you receive." },
];

const Features = () => (
  <section className="bg-paper py-24 md:py-32">
    <div className="container-px">
      <RevealGroup className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {featureCards.map((c) => (
          <RevealItem key={c.title} className="bg-white border border-ink/10 rounded-xl p-8 transition-transform duration-300 hover:-translate-y-1">
            <c.icon className="h-8 w-8 text-teal" strokeWidth={1.5} />
            <h3 className="mt-6 font-display font-700 text-2xl">{c.title}</h3>
            <p className="mt-4 text-ink/60 leading-relaxed">{c.body}</p>
          </RevealItem>
        ))}
      </RevealGroup>
    </div>
  </section>
);

const Lifecycle = () => (
  <section className="relative bg-ink text-white py-24 md:py-32 overflow-hidden">
    <div className="pointer-events-none absolute bottom-0 right-0 h-80 w-80 rounded-full bg-teal/12 blur-[130px]" />
    <div className="relative container-px">
      <Reveal><SectionLabel dark>Campaign Lifecycle</SectionLabel></Reveal>
      <Reveal delay={0.05}>
        <h2 className="mt-6 font-display font-800 text-4xl md:text-6xl tracking-tight max-w-3xl text-balance">
          From upload to playback evidence.
        </h2>
      </Reveal>
      <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 border-t border-l border-white/10">
        {CAMPAIGN_LIFECYCLE.map((s) => (
          <div key={s.n} className="border-r border-b border-white/10 p-7">
            <span className="font-display font-800 text-teal text-xl">{s.n}</span>
            <h4 className="mt-4 font-display font-700 text-lg">{s.t}</h4>
            <p className="mt-2 text-sm text-white/55 leading-relaxed">{s.d}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Roadmap = () => (
  <section className="bg-white py-24 md:py-32 border-y border-ink/10">
    <div className="container-px">
      <div className="flex items-center gap-3">
        <Sparkles className="h-5 w-5 text-amber" />
        <SectionLabel>Coming Soon · Roadmap</SectionLabel>
      </div>
      <Reveal delay={0.05}>
        <h2 className="mt-6 font-display font-800 text-4xl md:text-5xl tracking-tight max-w-3xl text-balance">
          Built into the architecture. Rolling out as we scale.
        </h2>
      </Reveal>
      <RevealGroup className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-4">
        <RevealItem className="flex items-start gap-3 border border-teal/40 bg-teal/5 rounded-lg p-5" data-testid="roadmap-live-feeds">
          <span className="mt-0.5 rounded bg-teal/15 text-teal font-mono text-[10px] uppercase tracking-wider px-2 py-1">Live</span>
          <span className="text-ink/85">Community feeds on every screen - live weather &amp; AQI for residents</span>
        </RevealItem>
        {ROADMAP.map((r, i) => (
          <RevealItem key={i} className="flex items-start gap-3 border border-dashed border-ink/20 rounded-lg p-5">
            <span className="mt-0.5 rounded bg-amber/15 text-amber font-mono text-[10px] uppercase tracking-wider px-2 py-1">Soon</span>
            <span className="text-ink/75">{r}</span>
          </RevealItem>
        ))}
      </RevealGroup>
    </div>
  </section>
);

const StartCta = () => (
  <section className="bg-paper py-24 md:py-32">
    <div className="container-px grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
      <div className="lg:col-span-5">
        <Reveal><SectionLabel>Start Advertising</SectionLabel></Reveal>
        <Reveal delay={0.05}>
          <h2 className="mt-6 font-display font-800 text-4xl md:text-5xl tracking-tight text-balance">
            Start your first campaign.
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <ul className="mt-8 space-y-3">
            {["Self-serve for local businesses", "Bulk booking for brands & agencies", "GST-compliant billing", "Playback evidence"].map((t) => (
              <li key={t} className="flex gap-3 text-ink/70"><Check className="h-5 w-5 text-teal shrink-0 mt-0.5" strokeWidth={2} />{t}</li>
            ))}
          </ul>
        </Reveal>
      </div>
      <div className="lg:col-span-7">
        <LeadForm variant="advertiser" ctaLabel="Talk to sales" testidPrefix="advertiser-lead"
          note="Tell us about your campaign and our team will help you go live." />
      </div>
    </div>
  </section>
);

const AdvFaq = () => (
  <section className="bg-white py-24 md:py-32 border-y border-ink/10">
    <div className="container-px grid grid-cols-1 lg:grid-cols-12 gap-12">
      <div className="lg:col-span-4">
        <Reveal><SectionLabel>FAQ</SectionLabel></Reveal>
        <Reveal delay={0.05}>
          <h2 className="mt-6 font-display font-800 text-4xl md:text-5xl tracking-tight text-balance">
            Advertiser questions, answered.
          </h2>
        </Reveal>
      </div>
      <div className="lg:col-span-8">
        <RevealGroup className="divide-y divide-ink/10 border-t border-ink/10">
          {ADVERTISER_FAQ.map((f, i) => (
            <RevealItem key={i} className="py-6">
              <h3 className="font-display font-700 text-lg flex gap-3">
                <span className="text-teal font-mono text-sm mt-1">Q{i + 1}</span> {f.q}
              </h3>
              <p className="mt-3 pl-9 text-ink/60 leading-relaxed">{f.a}</p>
            </RevealItem>
          ))}
        </RevealGroup>
      </div>
    </div>
  </section>
);

export default function Advertisers() {
  return (
    <>
      <Seo title={SEO.advertisers.title} description={SEO.advertisers.description} />
      <PageHero
        label="For Advertisers"
        titleLines={["Reach Families", "Where They Live."]}
        accentIndex={1}
        subtitle="Book targeted ads on premium residential screens - transparent per-impression pricing, a self-serve platform, and playback evidence."
        image={IMAGES.heroLobby}
        imageAlt="An advertiser's ad running on an AdX lobby screen as residents pass by"
        imagePosition="center"
        badge={{ label: "Live creative", value: "Your ad, on-screen" }}
      >
        <div className="flex flex-col sm:flex-row gap-4">
          <CTAButton href={REGISTER_URL} variant="primary" data-testid="advertiser-hero-cta">Create account</CTAButton>
          <CTAButton to="/pricing" variant="ghostLight">See pricing</CTAButton>
        </div>
      </PageHero>
      <EditorialMarquee items={["Per-Impression", "Prepaid Wallet", "Playback Evidence", "Self-Serve", "24-Hour Go-Live"]} />
      <WhyDooh />
      <PricingHow />
      <Features />
      <Lifecycle />
      <Roadmap />
      <AdvFaq />
      <StartCta />
    </>
  );
}
